import type {CSSProperties, ReactElement} from 'react';
import {useSortable} from '@dnd-kit/sortable';
import {CSS} from '@dnd-kit/utilities';
import {Icon} from '@Components/icon-v2';
import {IconSize} from '@Components/icon-v2/icon.types';
import {ClickableDiv} from '@Components/clickable-div';
import {GROUP_HEADER_ROW_PREFIX, GROUP_ICON, GROUP_LABEL_KEY} from '../../layers-panel.types';
import {useGroupThumbDataURL} from '@Hooks/poster-editor/useGroupThumbDataURL';
import styles from './layer-group-row.module.scss';
import panelStyles from '../../layers-panel.module.scss';

interface LayerGroupRowProps {
  groupId: string;
  isExpanded: boolean;
  isSelected: boolean;
  isOverlay?: boolean;
  disableDrag?: boolean;
  onClick?: (groupId: string) => void;
}

export function LayerGroupRow({groupId, isExpanded, isSelected, isOverlay = false, disableDrag = false, onClick}: LayerGroupRowProps): ReactElement {
  const sortableId = `${GROUP_HEADER_ROW_PREFIX}${groupId}`;
  const {attributes, isDragging, listeners, setNodeRef, transform, transition} = useSortable({id: sortableId, disabled: disableDrag});
  const thumbSrc = useGroupThumbDataURL(groupId);

  const showDropPlaceholder = isDragging && !isOverlay;
  const hideOverlayClone = isDragging && isOverlay;

  const style: CSSProperties = {
    ...(hideOverlayClone && {opacity: 0}),
    transform: CSS.Translate.toString(transform),
    transition,
  };

  const onClickRow = (): void => {
    onClick?.(groupId);
  };

  const rowClasses = `${styles.row} _position-relative flexbox flex-items-center gap-2 spacing-p-2 ${isSelected ? styles.rowSelected : ''}`;
  const containerClasses = `${styles.container} _cursor-pointer flex-v-row ${showDropPlaceholder ? panelStyles.dragging : ''}`;

  const renderPreview = (): ReactElement => {
    if (thumbSrc) {
      return <img alt="" className={styles.thumb} draggable={false} src={thumbSrc} />;
    }
    return <Icon icon={GROUP_ICON} size={IconSize.SIZE_ICON_16} className={`${styles.icon} flex-shrink-0`} />;
  };

  const renderStackBars = (): ReactElement | null => {
    if (isExpanded) {
      return null;
    }
    return (
      <>
        <div className={`${styles.stackBar} ${styles.stackBar1} spacing-m-t-1 spacing-m-l-2 spacing-m-r-2`} />
        <div className={`${styles.stackBar} ${styles.stackBar2} spacing-m-t-1 spacing-m-l-4 spacing-m-r-4`} />
      </>
    );
  };

  const renderDropPlaceholder = (): ReactElement | null => {
    if (!showDropPlaceholder) {
      return null;
    }
    return <div className={panelStyles.dropPlaceholder} />;
  };

  const renderDragHandle = (): ReactElement => {
    return <Icon icon="icon-draggable" size={IconSize.SIZE_ICON_16} className={`${styles.dragHandle} flex-shrink-0`} />;
  };

  return (
    <ClickableDiv
      ref={setNodeRef}
      className={containerClasses}
      style={style}
      enableFocusOutline={false}
      onClick={onClickRow}
      aria-label={window.i18next.t(GROUP_LABEL_KEY)}
      aria-expanded={isExpanded}
      {...attributes}
      {...listeners}
    >
      <div className={rowClasses}>
        {renderDropPlaceholder()}
        <div className={`${styles.handleSlot} flex-center flex-shrink-0`}>{renderDragHandle()}</div>
        <div className={`${styles.thumbSlot} flex-center flex-1`}>{renderPreview()}</div>
        <div className={`${styles.trailingSlot} flex-center flex-shrink-0`}>{isSelected ? <Icon icon="icon-check" size={IconSize.SIZE_ICON_16} className={`${styles.checkmark} flex-shrink-0`} /> : null}</div>
      </div>
      {renderStackBars()}
    </ClickableDiv>
  );
}
